import {Platform} from 'react-native';
import {getShopKey} from './exchange';

const algo = {
  name: 'RSA-OAEP',
  modulusLength: 4096,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
} as AlgorithmIdentifier;

const algoPSS = {
  name: 'RSA-PSS',
  modulusLength: 4096,
  publicExponent: new Uint8Array([1, 0, 1]),
  hash: 'SHA-256',
} as AlgorithmIdentifier;

const keyEncAlgo = {
  name: 'RSA-OAEP',
};
const payloadKeyEncAlgo = {
  name: 'AES-GCM',
  length: 256,
};
const payloadEncAlgo = {
  name: 'AES-GCM',
};

const PSSParams = {
  name: 'RSA-PSS',
  saltLength: 32,
} as AlgorithmIdentifier;

export const maxSize = 10 * 1024 * 1024;

export async function generateNewKeyPair(
  seed?: string,
): Promise<{publicKey: string; privateKey: string} | void> {
  if (Platform.OS === 'web') {
    const keyPair = (await window.crypto.subtle.generateKey(
      algo,
      true,
      ['encrypt', 'decrypt'],
    )) as CryptoKeyPair;
    const kp = {
      publicKey: JSON.stringify(
        await window.crypto.subtle.exportKey(
          'jwk',
          keyPair.publicKey,
        ),
      ),
      privateKey: JSON.stringify(
        await window.crypto.subtle.exportKey(
          'jwk',
          keyPair.privateKey,
        ),
      ),
    };
    return kp;
  }
}

export async function generateNewPSSPair(
  seed?: string,
): Promise<{publicKey: string; privateKey: string} | void> {
  if (Platform.OS === 'web') {
    const keyPair = (await window.crypto.subtle.generateKey(
      algoPSS,
      true,
      ['sign', 'verify'],
    )) as CryptoKeyPair;
    const kp = {
      publicKey: JSON.stringify(
        await window.crypto.subtle.exportKey(
          'jwk',
          keyPair.publicKey,
        ),
      ),
      privateKey: JSON.stringify(
        await window.crypto.subtle.exportKey(
          'jwk',
          keyPair.privateKey,
        ),
      ),
    };
    return kp;
  }
}

async function encodeBuffer(array: ArrayBuffer): Promise<string> {
  return new Promise(resolve => {
    const blob = new Blob([array]);
    const reader = new FileReader();

    reader.onload = event => {
      const dataUrl = reader.result as string;
      const [_, base64] = dataUrl.split(',');

      resolve(base64 as string);
    };

    reader.readAsDataURL(blob);
  });
}

function base64ToArrayBuffer(base64: string) {
  const binaryString = atob(base64);
  let bytes = new Uint8Array(binaryString.length);
  for (var i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export function getHexFromArrayBuffer(array: ArrayBuffer) {
  const hashArray = Array.from(new Uint8Array(array));
  const digest = hashArray
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return digest;
}

export async function getHexFromFile(
  file: File | Blob,
): Promise<string> {
  return new Promise(res => {
    var fr = new FileReader();

    fr.addEventListener('load', function () {
      let u = new Uint8Array(this.result),
        a = new Array(u.length),
        i = u.length;
      while (i--)
        // map to hex
        a[i] = (u[i] < 16 ? '0' : '') + u[i].toString(16);
      u = null; // free memory
      res(a.join(''));
    });
    fr.readAsArrayBuffer(file);
  });
}

export function hexToArrayBuffer(hex: string) {
  const buff = new Uint8Array(
    (hex.match(/[\da-f]{2}/gi) as string[]).map(function (h) {
      return parseInt(h, 16);
    }),
  );
  return buff;
}

export async function generateAesKey() {
  const key = await window.crypto.subtle.generateKey(
    payloadKeyEncAlgo,
    true,
    ['encrypt', 'decrypt'],
  );
  // console.info('aeskey', key);
  const kp = JSON.stringify(
    await window.crypto.subtle.exportKey('jwk', key),
  );
  return {kp, key};
}

// generateAesKey();

export async function decryptAesKey(
  key: string,
  privateKey: JsonWebKey,
) {
  try {
    const k = await window.crypto.subtle.importKey(
      'jwk',
      privateKey,
      algo,
      true,
      ['decrypt'],
    );
    const encoded = hexToArrayBuffer(key);
    const decrypted = await window.crypto.subtle.decrypt(
      keyEncAlgo,
      k,
      encoded,
    );
    console.info('decryptedKey', decrypted);
    const decoder = new TextDecoder();
    const decode = JSON.parse(decoder.decode(decrypted, {}));
    console.info('textdecoder decode', decode);
    const aesKey = await window.crypto.subtle.importKey(
      'jwk',
      decode,
      payloadKeyEncAlgo,
      true,
      ['encrypt', 'decrypt'],
    );
    return aesKey;
  } catch (err) {
    console.error('decryptkey error', err);
    throw err;
  }
}

export async function decryptPayload(
  k: CryptoKey,
  payload: string,
  ivb: string,
): Promise<string> {
  const iv = hexToArrayBuffer(ivb);
  const encoded = hexToArrayBuffer(payload);
  // console.info('encoded into buffer', encoded);
  const decrypted = await window.crypto.subtle.decrypt(
    {...payloadEncAlgo, iv},
    k,
    encoded,
  );
  // console.info('decryptedPayload', decrypted);
  const decoder = new TextDecoder();
  const decode = decoder.decode(decrypted);
  return decode;
}

export async function encryptAesKey(key: string, pubKey: JsonWebKey) {
  const k = await window.crypto.subtle.importKey(
    'jwk',
    pubKey,
    algo,
    true,
    ['encrypt'],
  );
  const encoder = new TextEncoder();
  const encoded = encoder.encode(key);
  const encrypted = await window.crypto.subtle.encrypt(
    keyEncAlgo,
    k,
    encoded,
  );
  // const decoder = new TextDecoder();
  // const decode = decoder.decode(encrypted)
  return getHexFromArrayBuffer(encrypted);
}

export async function encryptPayload(k: CryptoKey, payload: string) {
  try {
    const encoder = new TextEncoder();
    const encoded = encoder.encode(payload);
    // console.info('encoded into buffer', encoded);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await window.crypto.subtle.encrypt(
      {...payloadEncAlgo, iv},
      k,
      encoded,
    );
    // console.info('encrypted', encrypted);
    return {
      encryptedPayload: getHexFromArrayBuffer(encrypted),
      buffer: encrypted,
      iv: getHexFromArrayBuffer(iv.buffer),
    };
  } catch (err) {
    console.error('encrypt payload err', err);
    throw err;
  }
}

export async function encryptPayloadRaw(
  k: CryptoKey,
  payload: ArrayBuffer,
) {
  try {
    // console.info('encoded into buffer', encoded);
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await window.crypto.subtle.encrypt(
      {...payloadEncAlgo, iv},
      k,
      payload,
    );
    // console.info('encrypted', encrypted);
    return {
      encryptedPayload: getHexFromArrayBuffer(encrypted),
      buffer: encrypted,
      iv: getHexFromArrayBuffer(iv.buffer),
    };
  } catch (err) {
    console.error('encrypt payload err', err);
    throw err;
  }
}

export async function hashData(algo = 'SHA-256', payload: string) {
  const encoder = new TextEncoder();
  const encoded = encoder.encode(payload);
  const hash = await window.crypto.subtle.digest(algo, encoded);
  return getHexFromArrayBuffer(hash);
}

export async function verifySignature(
  key: JsonWebKey,
  signature: string,
  payload: string,
) {
  console.info('verify with key', key);
  const k = await window.crypto.subtle.importKey(
    'jwk',
    key,
    algoPSS,
    true,
    ['verify'],
  );
  const encoder = new TextEncoder();
  const encoded = encoder.encode(payload);
  const encodedSign = hexToArrayBuffer(signature);
  const verified = await window.crypto.subtle.verify(
    PSSParams,
    k,
    encodedSign,
    encoded,
  );
  return verified;
}

export async function signData(key: JsonWebKey, payload: string) {
  const k = await window.crypto.subtle.importKey(
    'jwk',
    key,
    algoPSS,
    true,
    ['sign'],
  );
  const encoder = new TextEncoder();
  const encoded = encoder.encode(payload);
  const sign = await window.crypto.subtle.sign(PSSParams, k, encoded);
  return getHexFromArrayBuffer(sign);
}

export function getKeyMaterial(password: string) {
  const enc = new TextEncoder();
  return window.crypto.subtle.importKey(
    'raw',
    enc.encode(password),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey'],
  );
}

export const createChunks = (file: File) => {
  const cSize = maxSize;
  let startPointer = 0;
  let endPointer = file.size;
  let chunks = [];
  while (startPointer < endPointer) {
    let newStartPointer = startPointer + cSize;
    chunks.push(file.slice(startPointer, newStartPointer));
    startPointer = newStartPointer;
  }
  console.info(
    `total chunks: ${chunks.length} for file ${file.name}`,
  );
  return chunks;
};

export async function encryptWithPassword(
  password: string,
  plaintext: string,
) {
  const keyMaterial = await getKeyMaterial(password);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const salt = window.crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const dec = new TextDecoder();
  const key = await window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100000,
      hash: 'SHA-256',
    },
    keyMaterial,
    {name: 'AES-GCM', length: 256},
    true,
    ['encrypt', 'decrypt'],
  );

  const encrypted = await window.crypto.subtle.encrypt(
    {name: 'AES-GCM', iv},
    key,
    enc.encode(plaintext),
  );
  return {
    encrypted: getHexFromArrayBuffer(encrypted),
    iv: getHexFromArrayBuffer(iv),
    salt: getHexFromArrayBuffer(salt),
  };
}
